// Admin Portal Route Configuration
import { lazy } from 'react';

// Lazy load Admin Portal components
const AdminDashboard = lazy(() => import('../../portals/admin/pages/dashboard/AdminDashboard'));

// Management Components (domain-organized)
const UserManagement = lazy(() => import('../../portals/admin/pages/users/UserManagementPage'));
const GroupManagement = lazy(() => import('../../portals/admin/components/groups/UserGroups'));
const SalesRepManagement = lazy(
  () => import('../../portals/admin/components/sales-reps/ManageSalesReps')
);
const GeofenceManagement = lazy(
  () => import('../../portals/admin/components/geofences/ManageGeofences')
);
// Organization Management - Tabbed page (Organizations, Vehicles, Devices)
const OrganizationManagement = lazy(
  () => import('../../portals/admin/pages/organizations/OrganizationManagementPage')
);
const TechnicianManagement = lazy(
  () => import('../../portals/admin/components/technicians/ManageTechnicians')
);
const BranchManagement = lazy(() => import('../../portals/admin/components/branches/ManageBranches'));

// Settings Dashboard
const AdminSettings = lazy(() => import('../../portals/admin/pages/settings/AdminSettings'));
const MySupportPage = lazy(() => import('../../shared/support/pages/MySupportPage'));
const OperationsCenter = lazy(
  () => import('../../portals/admin/pages/operations/OperationsCenter')
);
const ReportsAnalytics = lazy(() => import('../../portals/admin/pages/reports/ReportsAnalytics'));

// RBAC System Overview Page
const RBACSystemOverview = lazy(() => import('../../portals/admin/pages/rbac/RBACSystemOverview'));

// Role Management - Redirects to User Management page (roles tab)
const RolesRedirect = lazy(() => import('../../portals/admin/pages/users/RolesRedirect'));

// Organization Consolidation (Phase 2)
const OrganizationConsolidationPage = lazy(
  () => import('../../portals/admin/components/consolidation/OrganizationConsolidationPage')
);

// Group Linking (Duplicate Detection & Contact Sharing)
const GroupLinkingPage = lazy(
  () => import('../../portals/admin/components/linking/GroupLinkingPage')
);

// Development Dashboard
const DevelopmentDashboard = lazy(
  () => import('../../portals/admin/components/development/DevelopmentDashboard')
);

// Suppression Management Page
const SuppressionManagementPage = lazy(
  () => import('../../portals/admin/components/suppression/SuppressionManagementPage')
);

// Sync Management Page
const SyncManagement = lazy(() => import('../../portals/admin/pages/sync/SyncManagement'));

// Task Management Page
const AdminTaskManagement = lazy(
  () => import('../../portals/admin/pages/tasks/AdminTaskManagement')
);

// Group Management - Organization Hierarchy Redesign
const PendingGroups = lazy(() => import('../../portals/admin/pages/groups/PendingGroups'));
const OrganizationMerge = lazy(() => import('../../portals/admin/pages/groups/OrganizationMerge'));

/**
 * Admin Portal Routes Configuration
 * For system administrators and management
 */
export const adminRoutes = {
  path: '/admin',
  name: 'Admin Portal',
  description: 'Portal for system administrators and management',
  requiredRoles: ['Admin', 'Super Admin'],
  requiredPermissions: ['admin.access'],
  children: [
    {
      index: true,
      component: AdminDashboard,
      name: 'Dashboard',
      description: 'Administrative dashboard',
      requiredRoles: ['Admin', 'Super Admin'],
      meta: {
        title: 'Admin Dashboard',
        breadcrumb: 'Dashboard',
        icon: 'dashboard',
      },
    },
    {
      path: 'users',
      component: UserManagement,
      name: 'User & Role Management',
      description: 'Manage system users, roles and permissions',
      requiredRoles: ['Admin', 'Super Admin'],
      meta: {
        title: 'User & Role Management',
        breadcrumb: 'Users',
        icon: 'person',
      },
    },
    {
      path: 'groups',
      component: GroupManagement,
      name: 'Groups & Roles',
      description: 'Manage user groups and roles',
      requiredRoles: ['Admin', 'Super Admin'],
      meta: {
        title: 'Groups & Roles',
        breadcrumb: 'Groups',
        icon: 'group',
      },
    },
    {
      path: 'sales-reps',
      component: SalesRepManagement,
      name: 'Sales Representatives',
      description: 'Manage sales representatives and team assignments',
      requiredRoles: ['Admin', 'Super Admin'],
      meta: {
        title: 'Sales Representatives',
        breadcrumb: 'Sales Reps',
        icon: 'person_outline',
      },
    },
    {
      path: 'organizations',
      component: OrganizationManagement,
      name: 'Organization Management',
      description: 'Manage organizations, vehicles, and devices',
      requiredRoles: ['Admin', 'Super Admin'],
      meta: {
        title: 'Organization Management',
        breadcrumb: 'Organizations',
        icon: 'business',
      },
    },
    {
      path: 'technicians',
      component: TechnicianManagement,
      name: 'Technician Management',
      description: 'Manage technicians',
      requiredRoles: ['Admin', 'Super Admin'],
      meta: {
        title: 'Technician Management',
        breadcrumb: 'Technicians',
        icon: 'build',
      },
    },
    {
      path: 'branches',
      component: BranchManagement,
      name: 'Branch Management',
      description: 'Manage branches',
      requiredRoles: ['Admin', 'Super Admin'],
      meta: {
        title: 'Branch Management',
        breadcrumb: 'Branches',
        icon: 'store',
      },
    },
    {
      path: 'geofences',
      component: GeofenceManagement,
      name: 'Geofence Management',
      description: 'Manage geographical boundaries and location monitoring',
      requiredRoles: ['Admin', 'Super Admin'],
      meta: {
        title: 'Geofence Management',
        breadcrumb: 'Geofences',
        icon: 'location_on',
      },
    },
    {
      path: 'tasks',
      component: AdminTaskManagement,
      name: 'Task Management',
      description: 'Create, assign, and manage bureau user tasks',
      requiredRoles: ['Admin', 'Super Admin'],
      meta: {
        title: 'Task Management',
        breadcrumb: 'Tasks',
        icon: 'assignment',
      },
    },
    {
      path: 'operations',
      component: OperationsCenter,
      name: 'Operations Center',
      description: 'Operational tools and monitoring',
      requiredRoles: ['Admin', 'Super Admin'],
      meta: {
        title: 'Operations Center',
        breadcrumb: 'Operations',
        icon: 'control_point',
      },
      children: [
        {
          path: 'testing',
          component: lazy(() => import('../../portals/admin/pages/testing/TestingOperations')),
          name: 'Testing Operations',
          requiredRoles: ['Admin', 'Super Admin'],
          meta: {
            title: 'Testing Operations',
            breadcrumb: 'Testing',
          },
        },
        {
          path: 'maintenance',
          component: lazy(() => import('../../portals/admin/pages/maintenance/MaintenanceMode')),
          name: 'Maintenance Mode',
          requiredRoles: ['Admin', 'Super Admin'],
          meta: {
            title: 'Maintenance Mode',
            breadcrumb: 'Maintenance',
          },
        },
      ],
    },
    {
      path: 'reports',
      component: ReportsAnalytics,
      name: 'Reports & Analytics',
      description: 'System reports and analytics',
      requiredRoles: ['Admin', 'Super Admin'],
      meta: {
        title: 'Reports & Analytics',
        breadcrumb: 'Reports',
        icon: 'analytics',
      },
      children: [
        {
          path: 'users',
          component: lazy(() => import('../../portals/admin/pages/reports/UserReports')),
          name: 'User Reports',
          requiredRoles: ['Admin', 'Super Admin'],
          meta: {
            title: 'User Reports',
            breadcrumb: 'User Reports',
          },
        },
        {
          path: 'fleet',
          component: lazy(() => import('../../portals/admin/pages/reports/FleetReports')),
          name: 'Fleet Reports',
          requiredRoles: ['Admin', 'Super Admin'],
          meta: {
            title: 'Fleet Reports',
            breadcrumb: 'Fleet Reports',
          },
        },
        {
          path: 'system',
          component: lazy(() => import('../../portals/admin/pages/reports/SystemReports')),
          name: 'System Reports',
          requiredRoles: ['Admin', 'Super Admin'],
          meta: {
            title: 'System Reports',
            breadcrumb: 'System Reports',
          },
        },
      ],
    },
    {
      path: 'rbac-overview',
      component: RBACSystemOverview,
      name: 'RBAC System Overview',
      description: 'Visual overview of RBAC system architecture and flows',
      requiredRoles: ['Admin', 'Super Admin'],
      meta: {
        title: 'RBAC System Overview',
        breadcrumb: 'RBAC Overview',
        icon: 'security',
      },
    },
    {
      path: 'roles',
      component: RolesRedirect,
      name: 'Role Management',
      description: 'Redirects to User & Role Management page',
      requiredRoles: ['Admin', 'Super Admin'],
      meta: {
        title: 'Role Management',
        breadcrumb: 'Roles',
        icon: 'admin_panel_settings',
        hideInNavigation: true,
      },
    },
    {
      path: 'organization-consolidation',
      component: OrganizationConsolidationPage,
      name: 'Organization Consolidation',
      description: 'AI-powered organization consolidation analysis and management',
      requiredRoles: ['Admin', 'Super Admin'],
      meta: {
        title: 'Organization Consolidation',
        breadcrumb: 'Consolidation',
        icon: 'merge_type',
      },
    },
    {
      path: 'group-linking',
      component: GroupLinkingPage,
      name: 'Group Linking',
      description: 'Link similar groups to share contacts for alerts',
      requiredRoles: ['Admin', 'Super Admin'],
      meta: {
        title: 'Group Linking',
        breadcrumb: 'Group Linking',
        icon: 'link',
      },
    },
    {
      path: 'development',
      component: DevelopmentDashboard,
      name: 'Development Tools',
      description: 'Development tools and testing interfaces',
      requiredRoles: ['Admin', 'Super Admin'],
      meta: {
        title: 'Development Dashboard',
        breadcrumb: 'Development',
        icon: 'code',
      },
    },
    {
      path: 'suppression',
      component: SuppressionManagementPage,
      name: 'Suppression Management',
      description: 'Configure alert suppression profiles and rules',
      requiredRoles: ['Admin', 'Super Admin'],
      meta: {
        title: 'Suppression Management',
        breadcrumb: 'Suppression',
        icon: 'tune',
      },
    },
    {
      path: 'sync-management',
      component: SyncManagement,
      name: 'Sync Management',
      description: 'Monitor and manage external API synchronization',
      requiredRoles: ['Admin', 'Super Admin'],
      meta: {
        title: 'Sync Management',
        breadcrumb: 'Sync Management',
        icon: 'sync',
      },
    },
    {
      path: 'pending-groups',
      component: PendingGroups,
      name: 'Pending Groups',
      description: 'Review and verify groups synced from VMS/CMS',
      requiredRoles: ['Admin', 'Super Admin'],
      meta: {
        title: 'Pending Groups Verification',
        breadcrumb: 'Pending Groups',
        icon: 'pending_actions',
      },
    },
    {
      path: 'organization-merge',
      component: OrganizationMerge,
      name: 'Organization Merge',
      description: 'Merge duplicate organizations and consolidate groups',
      requiredRoles: ['Admin', 'Super Admin'],
      meta: {
        title: 'Organization Merge Tool',
        breadcrumb: 'Organization Merge',
        icon: 'merge',
      },
    },
    {
      path: 'settings',
      component: AdminSettings,
      name: 'Settings Dashboard',
      description: 'Configure and manage all admin portal settings',
      requiredRoles: ['Admin', 'Super Admin'],
      meta: {
        title: 'Settings Dashboard',
        breadcrumb: 'Settings',
        icon: 'settings',
      },
    },
    {
      path: 'my-support',
      component: MySupportPage,
      name: 'My Support',
      description: 'My tickets and notifications',
      requiredRoles: ['Admin', 'Super Admin'],
      meta: {
        title: 'My Support',
        breadcrumb: 'Support',
        icon: 'support_agent',
      },
    },
  ],
};

export default adminRoutes;
